import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { ViewList } from "@mui/icons-material";
import { Box, ButtonBase, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";

import { useMultiAnalysis } from "../../../../../../context/multi-analysis-context";
import { useTimePanelDataMarks } from "../../hooks/use-time-panel-data-marks";
import { useTimePanelMarkSelection } from "../../hooks/use-time-panel-mark-selection";
import type { IChartData } from "../../../scheme/components/analytics/interfaces";
import type { IMultiAnalysisTimeRange } from "../../types";
import {
  buildTimePanelMeasureListItems,
  pruneTimePanelMeasureKeys,
  toggleTimePanelMeasureKey,
} from "../../utils/time-panel-measures";
import { MultiAnalysisPanel } from "../panel";
import { TimePanelChart, TIME_PANEL_CHART_MIN_HEIGHT } from "./time-panel-chart";

interface IMultiAnalysisTimePanelProps {
  globalRange: IMultiAnalysisTimeRange;
  trendSeries: IChartData[];
  isTrendSeriesLoading: boolean;
  activeMeasureFilters: Record<string, unknown>;
  isTrainSelected: boolean;
}

export const MultiAnalysisTimePanel: FC<IMultiAnalysisTimePanelProps> = ({
  globalRange,
  trendSeries,
  isTrendSeriesLoading,
  activeMeasureFilters,
  isTrainSelected,
}) => {
  const { formatMessage } = useIntl();
  const [measureListOpen, setMeasureListOpen] = useState(false);
  const [selectedMeasureKeys, setSelectedMeasureKeys] = useState<Set<string>>(() => new Set());
  const [hiddenMeasureKeys, setHiddenMeasureKeys] = useState<Set<string>>(() => new Set());
  const measureClickTimeoutRef = useRef<number | null>(null);
  const {
    selectedPoints,
    activeMeasureType,
    frameRange,
    setFrameRange,
    resetFrameRange,
    setDataMarks,
    setSelectedDataMarks,
    selectedDataMarks,
    isTemplateEditMode,
  } = useMultiAnalysis();

  const { dataMarks, isLoading: isDataMarksLoading } = useTimePanelDataMarks({
    enabled: isTrainSelected,
    points: selectedPoints,
    globalRange,
    activeMeasureType,
    cellFilters: activeMeasureFilters,
  });

  const measureListItems = useMemo(
    () => buildTimePanelMeasureListItems(dataMarks, trendSeries),
    [dataMarks, trendSeries],
  );

  useEffect(() => {
    setDataMarks(dataMarks);
  }, [dataMarks, setDataMarks]);

  useEffect(() => {
    const availableKeys = new Set(measureListItems.map(item => item.key));

    setSelectedMeasureKeys(current => {
      const next = pruneTimePanelMeasureKeys(current, availableKeys);

      return next.size === current.size ? current : next;
    });
    setHiddenMeasureKeys(current => {
      const next = pruneTimePanelMeasureKeys(current, availableKeys);

      return next.size === current.size ? current : next;
    });
  }, [measureListItems]);

  useEffect(
    () => () => {
      if (measureClickTimeoutRef.current != null) {
        window.clearTimeout(measureClickTimeoutRef.current);
      }
    },
    [],
  );

  const { handleMarkClick } = useTimePanelMarkSelection({
    dataMarks,
    frameRange,
    selectedDataMarks,
    setSelectedDataMarks,
  });

  const handleMeasureClick = (key: string) => {
    if (measureClickTimeoutRef.current != null) {
      window.clearTimeout(measureClickTimeoutRef.current);
    }

    measureClickTimeoutRef.current = window.setTimeout(() => {
      measureClickTimeoutRef.current = null;
      setSelectedMeasureKeys(current => toggleTimePanelMeasureKey(current, key));
    }, 220);
  };

  const handleMeasureDoubleClick = (key: string) => {
    if (measureClickTimeoutRef.current != null) {
      window.clearTimeout(measureClickTimeoutRef.current);
      measureClickTimeoutRef.current = null;
    }

    setHiddenMeasureKeys(current => toggleTimePanelMeasureKey(current, key));
  };

  const selectedMarkKeys = new Set(selectedDataMarks.map(mark => mark.key));
  const measureListToggleLabel = formatMessage({
    id: measureListOpen
      ? "pages.multiAnalysis.timePanel.hideMeasureList"
      : "pages.multiAnalysis.timePanel.showMeasureList",
  });
  const hasMeasureList = measureListItems.length > 0;

  return (
    <ProgressOverlay
      isLoading={isTrendSeriesLoading || isDataMarksLoading}
      wrapperSx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} sx={{ height: "100%", minHeight: 0, minWidth: 0 }}>
          <Box
            sx={{
              flex: 1,
              minHeight: `max(100%, ${TIME_PANEL_CHART_MIN_HEIGHT}px)`,
              minWidth: 0,
              height: "100%",
              position: "relative",
            }}
          >
            <TimePanelChart
              globalRange={globalRange}
              frameRange={frameRange}
              trendSeries={trendSeries}
              dataMarks={dataMarks}
              selectedMarkKeys={selectedMarkKeys}
              selectedMeasureKeys={selectedMeasureKeys}
              hiddenMeasureKeys={hiddenMeasureKeys}
              hasSelectedPoints={selectedPoints.length > 0}
              onFrameRangeChange={setFrameRange}
              onFrameRangeReset={() => resetFrameRange(globalRange)}
              onMarkClick={handleMarkClick}
            />
          </Box>
          {isTemplateEditMode ? null : (
            <Box
              sx={{
                flex: { xs: "0 0 auto", md: measureListOpen ? "0 0 260px" : "0 0 40px" },
                width: { xs: "100%", md: measureListOpen ? 260 : 40 },
                maxHeight: { xs: measureListOpen ? 180 : 40, md: "100%" },
                minHeight: 0,
                borderTop: { xs: "1px solid #d8ddea", md: 0 },
                borderLeft: { xs: 0, md: "1px solid #d8ddea" },
                bgcolor: "#f5f7fb",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  flex: "0 0 auto",
                  display: "flex",
                  justifyContent: measureListOpen ? "flex-end" : "center",
                  px: 0.25,
                  py: 0.25,
                }}
              >
                <Tooltip title={measureListToggleLabel}>
                  <span>
                    <IconButton
                      aria-label={measureListToggleLabel}
                      size="small"
                      color={measureListOpen ? "primary" : "default"}
                      disabled={!hasMeasureList}
                      onClick={() => setMeasureListOpen(value => !value)}
                    >
                      <ViewList fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {measureListOpen ? (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    bgcolor: "#fff",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {hasMeasureList ? (
                    measureListItems.map(item => {
                      const isSelected = selectedMeasureKeys.has(item.key);
                      const isHidden = hiddenMeasureKeys.has(item.key);
                      const rawUnits = item.units.trim();
                      const numericUnits = Number(rawUnits);
                      const unitCode =
                        item.unitCode ?? (rawUnits && Number.isFinite(numericUnits) ? numericUnits : null);
                      const unitLabel =
                        rawUnits && !Number.isFinite(numericUnits)
                          ? rawUnits
                          : unitCode != null
                            ? formatMessage({ id: `enums.units.${unitCode}` })
                            : "";

                      return (
                        <ButtonBase
                          key={item.key}
                          aria-pressed={isSelected}
                          title={formatMessage({
                            id: isHidden
                              ? "pages.multiAnalysis.timePanel.showMeasureOnChart"
                              : "pages.multiAnalysis.timePanel.hideMeasureOnChart",
                          })}
                          onClick={() => handleMeasureClick(item.key)}
                          onDoubleClick={() => handleMeasureDoubleClick(item.key)}
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            px: 1,
                            py: 0.75,
                            textAlign: "left",
                            borderBottom: "1px solid #edf0f7",
                            opacity: isHidden ? 0.38 : selectedMeasureKeys.size === 0 || isSelected ? 1 : 0.45,
                            bgcolor: isHidden ? "#f6f7fb" : isSelected ? "#eef3fb" : "#fff",
                            "&:hover": {
                              bgcolor: "#f0f4fc",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              flex: "0 0 auto",
                              width: 10,
                              height: 10,
                              mt: 0.45,
                              borderRadius: "50%",
                              bgcolor: item.color,
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#2e3445",
                                textDecoration: isHidden ? "line-through" : "none",
                              }}
                              noWrap
                            >
                              {item.pointName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#667085",
                                textDecoration: isHidden ? "line-through" : "none",
                              }}
                              noWrap
                            >
                              {item.measureName}
                            </Typography>
                            {unitLabel ? (
                              <Typography sx={{ fontSize: 11, color: "#2e3445", fontWeight: 600 }} noWrap>
                                {unitLabel}
                              </Typography>
                            ) : null}
                          </Box>
                        </ButtonBase>
                      );
                    })
                  ) : (
                    <Typography sx={{ p: 1.5, fontSize: 12, color: "#8b93a7" }}>
                      {formatMessage({ id: "pages.multiAnalysis.timePanel.emptyMeasureList" })}
                    </Typography>
                  )}
                </Box>
              ) : null}
            </Box>
          )}
        </Stack>
      </Box>
    </ProgressOverlay>
  );
};

interface IMultiAnalysisTimePanelPanelProps extends IMultiAnalysisTimePanelProps {
  expandedFlexGrow?: number;
  sx?: SxProps<Theme>;
}

export const MultiAnalysisTimePanelPanel: FC<IMultiAnalysisTimePanelPanelProps> = ({
  sx,
  expandedFlexGrow = 1,
  ...timePanelProps
}) => (
  <MultiAnalysisPanel
    hideHeader
    sx={[
      {
        flex: `${expandedFlexGrow} 1 0`,
        minHeight: 0,
        height: "100%",
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <MultiAnalysisTimePanel {...timePanelProps} />
  </MultiAnalysisPanel>
);
